import React, { useState } from 'react';
import { useStore } from '../../store/StoreContext';
import { Step2Workflows } from '../ProjectBuilder/Step2Workflows';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { formatDuration, generateId } from '../../utils/helpers';
import { Button } from '../../components/UI/Button';
import { CategoryModal } from '../ProjectBuilder/CategoryModal';

export const WorkflowsPage = () => {
  const { state, addEntity, deleteEntity } = useStore();
  const [view, setView] = useState('list');
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);

  const workflows = Object.values(state.workflowTemplates || {});
  const workflowCategories = Object.values(state.categories || {}).filter(c => c.type === 'workflow');
  const filteredWorkflows = workflows.filter(wf => activeTab === 'all' || wf.categoryId === activeTab);

  const handleEdit = (wf) => {
    setEditingWorkflow(wf);
    setView('editor');
  };

  const handleCopyWorkflow = async (wf) => {
    const newWf = {
      ...wf,
      id: generateId('w'),
      name: `${wf.name} (Copy)`,
      tasks: wf.tasks.map(t => ({ ...t, id: generateId('ti') })),
      created_at: new Date().toISOString()
    };
    await addEntity('workflowTemplates', newWf);
  };

  const handleAddNew = () => {
    setEditingWorkflow(null);
    setView('editor');
  };

  const handleComplete = () => {
    setView('list');
    setEditingWorkflow(null);
  };

  if (view === 'editor') {
    return (
      <div className="h-full flex flex-col bg-base-900">
        <div className="p-4 border-b border-base-700 bg-base-800 flex justify-between items-center shrink-0">
          <Button variant="ghost" onClick={handleComplete}>
            <SafeIcon name="ArrowLeft" className="mr-2" /> Back to Library
          </Button>
          <div className="flex items-center gap-3">
            <SafeIcon name="GitMerge" className="text-primary w-5 h-5" />
            <h2 className="font-bold text-lg">
              {editingWorkflow ? `Editing: ${editingWorkflow.name}` : 'Create Workflow Template'}
            </h2>
          </div>
          <div className="w-32" />
        </div>
        <div className="flex-1 p-6 overflow-hidden">
          <Step2Workflows editingWorkflow={editingWorkflow} onComplete={handleComplete} />
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col bg-base-900">
      <div className="p-6 border-b border-base-700 bg-base-800 shrink-0">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <SafeIcon name="GitMerge" className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Workflow Templates</h1>
              <p className="text-sm text-gray-400">Standardized sequences of tasks for project phases.</p>
            </div>
          </div>
          <Button onClick={handleAddNew}>
            <SafeIcon name="Plus" className="mr-2" /> New Workflow
          </Button>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button 
            onClick={() => setActiveTab('all')} 
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${activeTab === 'all' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'bg-base-900 text-gray-400 hover:text-white border border-base-700'}`}
          >
            All Sequences
          </button>
          {workflowCategories.map(cat => (
            <div key={cat.id} className="flex items-center group shrink-0">
              <button 
                onClick={() => setActiveTab(cat.id)} 
                className={`px-4 py-2 rounded-l-lg text-xs font-bold flex items-center gap-2 border border-r-0 transition-all whitespace-nowrap ${activeTab === cat.id ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20' : 'bg-base-900 text-gray-400 hover:text-white border-base-700'}`}
              >
                <SafeIcon name={cat.icon || 'Layers'} className="w-3.5 h-3.5" /> {cat.name}
              </button>
              <button 
                onClick={() => { setEditingCat(cat); setIsCatModalOpen(true); }}
                className={`px-3 py-2 rounded-r-lg border transition-all ${activeTab === cat.id ? 'bg-primary text-white border-primary border-l-white/20' : 'bg-base-900 text-gray-500 hover:text-white border-base-700'}`}
              >
                <SafeIcon name="Edit2" className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button 
            onClick={() => { setEditingCat(null); setIsCatModalOpen(true); }}
            className="p-2 rounded-lg bg-base-900 border border-base-700 text-primary hover:bg-base-700 transition-colors shrink-0"
            title="Add Workflow Category"
          >
            <SafeIcon name="Plus" className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">
        {filteredWorkflows.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-gray-600 opacity-50 italic">
            <SafeIcon name="GitMerge" className="w-16 h-16 mb-4" />
            <p className="text-lg">No workflow templates found in this category</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkflows.map(wf => {
            const cat = state.categories[wf.categoryId];
            const totalMins = wf.tasks?.reduce((s, t) => s + (t.durationMinutes || 0), 0) || 0;
            return (
              <div key={wf.id} className="bg-base-800 rounded-2xl border border-base-700 p-6 flex flex-col hover:border-primary/50 transition-all shadow-xl group">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-3 rounded-xl bg-base-900 text-primary border border-base-700 shadow-inner group-hover:scale-110 transition-transform shrink-0">
                      <SafeIcon name={cat?.icon || 'GitMerge'} className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-white leading-tight truncate">{wf.name}</h3>
                      {cat && (
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-0.5 truncate">
                          <SafeIcon name={cat.icon || 'Layers'} className="w-3 h-3" /> {cat.name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => handleCopyWorkflow(wf)} className="p-2 text-gray-500 hover:text-accent hover:bg-accent/10 rounded-lg transition-all" title="Duplicate"><SafeIcon name="Copy" className="w-4 h-4" /></button>
                    <button onClick={() => handleEdit(wf)} className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-all" title="Edit"><SafeIcon name="Edit2" className="w-4 h-4" /></button>
                    <button onClick={() => { if (confirm('Delete this template?')) deleteEntity('workflowTemplates', wf.id); }} className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all" title="Delete"><SafeIcon name="Trash2" className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="flex items-center gap-4 mb-6 pt-4 border-t border-base-700">
                  <div className="text-[10px] font-mono text-gray-500 flex items-center gap-1.5 bg-base-900 px-2 py-1 rounded border border-base-700">
                    <SafeIcon name="Layers" className="w-3 h-3" /> {wf.tasks?.length || 0} Tasks
                  </div>
                  <div className="text-[10px] font-mono text-gray-500 flex items-center gap-1.5 bg-base-900 px-2 py-1 rounded border border-base-700">
                    <SafeIcon name="Clock" className="w-3 h-3" /> {formatDuration(totalMins)}
                  </div>
                </div>
                <div className="space-y-2">
                   <div className="text-[10px] uppercase tracking-[0.2em] text-gray-600 font-bold mb-2">Sequence Preview</div>
                   {wf.tasks?.slice(0, 3).map((t, i) => (
                     <div key={i} className="flex items-center gap-3 text-xs text-gray-400 bg-base-900/50 px-3 py-2 rounded-lg border border-base-700/50">
                       <span className="text-[10px] font-mono font-bold text-primary opacity-60 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                       <span className="truncate flex-1">{t.name}</span>
                       <span className="text-[9px] font-mono text-gray-600 shrink-0">{formatDuration(t.durationMinutes)}</span>
                     </div>
                   ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <CategoryModal isOpen={isCatModalOpen} onClose={() => setIsCatModalOpen(false)} editingCategory={editingCat} categoryType="workflow" />
    </motion.div>
  );
};